import { Fragment, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useSessionStorage } from 'react-use'
import clsx from 'clsx'

import { GetImageMetdataLink } from '@makyoapp/makyo-sdk'
import { FileCategory, IFilePresenter, IFolderBrief, ImageMetadata } from '@makyoapp/makyo-sdk/models/Files'
import { ImageArrayValue } from '@makyoapp/makyo-common'
import { Folder } from '@makyoapp/makyo-icon/ficon'
import { SearchAlt16, Upload16 } from '@makyoapp/makyo-icon/icon'
import { ACCEPT_IMAGES, Button, ButtonVariant, CloseCircle, Loading, Tabs, WrappedPagination } from '@makyoapp/makyo-ui'

import { useGetFolders, useGetStorage } from '@/resolvers/org/storage/query'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { ImageManagerKind, useImageManagerContext } from '@/context/ImageManagerContext'
import { useUploadContext } from '@/context/UploadContext'
import { STORAGE_KEYS } from '@/shared/constants'
import { getFileSize } from '@/shared/utils'

import { LazyImage } from '../LazyImage'

type ImageManagerState = {
  search: string
  page: number
  folderPath: { id: string; name: string }[]
  currentFolderId: string
}

export function MyImagesPanel() {
  const { t } = useTranslation()
  const { currentOrg } = useCurrentOrgContext()
  const { onChange, onHide, smallPreview, maxHeight, multiple, selectedImages, setSelectedImages } =
    useImageManagerContext()
  const { uploadFiles } = useUploadContext()

  const [uploading, setUploading] = useState<null | number>(null)
  const [hasInteracted, setHasInteracted] = useState(false)

  // ================================ persisted states ================================

  // initialize persisted states
  const [state, setState] = useSessionStorage(STORAGE_KEYS.IMAGE_MANAGER, {
    search: '',
    page: 1,
    folderPath: [],
    currentFolderId: '',
  } as ImageManagerState)

  const { search, page, folderPath, currentFolderId } = state

  const updateState = (newState: Partial<ImageManagerState>) => {
    setState({ ...state, ...newState })
  }

  // Manual debouncing for search
  const [debSearch, setDebSearch] = useState(search)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebSearch(search)
    }, 500)

    return () => clearTimeout(timer)
  }, [search])

  // ================================ queries ================================

  // Access org storage folders
  const storageFoldersQuery = useGetFolders(currentFolderId)
  const storageFolders = storageFoldersQuery.data || []

  // Access org storage files
  const storageQuery = useGetStorage({
    params: {
      page,
      search: debSearch,
      categories: [FileCategory.Image],
      folderId: debSearch ? 'all' : currentFolderId || undefined,
    },
  })
  // derived state
  const storage = storageQuery.data || { items: [], totalCount: 0 }

  // ================================ handlers ================================
  const handleClear = () => {
    setHasInteracted(true)
    setSelectedImages(() => [])
  }

  const handleImageSelection = (img: IFilePresenter, isSelected: boolean) => {
    setHasInteracted(true) // Mark that the user has interacted

    if (isSelected) {
      setSelectedImages((prev) => prev.filter((p) => p.Url !== img._Link))
    } else {
      setSelectedImages((prev) => [
        ...prev,
        {
          Url: img._Link,
          Title: '',
          Description: '',
          CreatedAt: img._CreatedAt,
          Owner: img._User?._Name,
          Size: img._Size,
          Width: 0,
          Height: 0,
          Tags: [],
          Format: '',
        } as ImageArrayValue,
      ])
    }
  }

  const handleUpload = async (files: File[]) => {
    if (!currentOrg) return
    setUploading(files.length)

    try {
      await uploadFiles(files, currentOrg.id, currentFolderId ?? '')
      // Remove refreshStorage() call here since it's handled by query invalidation
      handlePageChange(1)
    } catch (error) {
      console.error('Upload error:', error)
    } finally {
      setUploading(null)
    }
  }

  const handleNavigateToFolder = (folderId: string) => {
    const folder = storageFolders.find((f) => f._id === folderId)
    if (folder) {
      updateState({
        folderPath: [...folderPath, { id: folder._id, name: folder._Name }],
        currentFolderId: folderId,
        page: 1, // Reset to page 1 when navigating to a new folder
      })
    }
  }

  const handleNavigateToRoot = () => {
    updateState({
      folderPath: [],
      currentFolderId: '',
      page: 1, // Reset to page 1 when navigating to root
    })
  }

  const handleNavigateToPathFolder = (index: number) => {
    const newPath = folderPath.slice(0, index + 1)
    updateState({
      folderPath: newPath,
      currentFolderId: newPath[newPath.length - 1].id,
      page: 1,
    })
  }

  const handleSearchChange = (value: string) => {
    updateState({
      search: value,
      page: 1, // Reset to page 1 when search changes
    })
  }

  const handlePageChange = (newPage: number) => {
    updateState({
      page: newPage,
    })
  }

  // ================================ derived state ================================

  const isUnsplashImage = (url: string): boolean => {
    const unsplashRegex = /^https:\/\/images\.unsplash\.com\/photo-[a-zA-Z0-9_-]+\?.*$/
    return unsplashRegex.test(url)
  }

  // track initial selected images
  const initialSelectedRef = useRef(selectedImages)
  const myInitialImages = initialSelectedRef.current?.filter((img) => !isUnsplashImage(img.Url))
  const myImages = selectedImages.filter((img) => !isUnsplashImage(img.Url))

  const isSaveButtonDisabled =
    (!hasInteracted && myImages.length === 0) ||
    (hasInteracted && myInitialImages.length === 0 && myImages.length === 0)

  const isLoading = storageQuery.isLoading || storageFoldersQuery.isLoading

  return (
    <Tabs.Panel data-panel-id={ImageManagerKind.MyImages} noPadding eventKey={ImageManagerKind.MyImages}>
      <div className='h-10 flex items-center sticky top-0 bg-white dark:bg-slate-800 z-50'>
        <div className={clsx('h-10', 'mk-modal-box w-full flex-shrink-1')}>
          <SearchAlt16 className={clsx('fill-zinc-700 dark:fill-slate-300')} />
          <input
            placeholder={t('Search')}
            autoFocus
            onChange={(e) => handleSearchChange(e.target.value || '')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation()
              }
            }}
            value={search}
            type='input'
            name='search'
            autoComplete='off'
            className={clsx('mk-modal-search')}
          />
          <button
            type='button'
            className={clsx('mk-modal-search-clear', search ? 'visible' : 'invisible')}
            onClick={() => handleSearchChange('')}
          >
            <CloseCircle />
          </button>
        </div>
        <div
          className='border-b bdr h-10 flex-cc min-w-[2.5rem] shrink-0 flex-grow-1'
          style={{
            borderInlineStartWidth: '1px',
            borderInlineStartStyle: 'solid',
          }}
        >
          {uploading !== null ? (
            <div className='flex-c px-2 flex-grow-1'>
              <span className='loading spin shrink-0 grow-0' />
              <div className='whitespace-nowrap text-m-sm shrink-0 flex-grow-1'>
                <Trans values={{ items: uploading }}>Uploading {{ uploading }} Items</Trans>
              </div>
            </div>
          ) : (
            <div className='relative'>
              <Button
                id='upload-image-btn'
                variant={ButtonVariant.Normal}
                disabled={isLoading}
                noPadding
                icon={<Upload16 />}
                className='w-full flex h-10! items-center justify-center rounded-xs hover:border-primary-500 transition-colors text-xs p-4'
              >
                <span>Upload Image</span>
              </Button>
              <input
                type='file'
                multiple
                accept={ACCEPT_IMAGES}
                onChange={(e) => {
                  const files = Array.from(e.currentTarget.files ?? [])
                  handleUpload(files)
                }}
                className='hidden'
                disabled={isLoading}
                id='file-upload-input'
              />
              <label
                htmlFor='file-upload-input'
                className='absolute inset-0 cursor-pointer'
                aria-label='Upload image'
              />
            </div>
          )}
        </div>
      </div>

      {isLoading ? (
        <div
          className='flex-cc'
          style={{
            height: maxHeight ? maxHeight : '60vh',
          }}
        >
          <Loading fullHeight={false} />
        </div>
      ) : (
        <>
          {/* Folder breadcrumbs */}
          <div className='px-4 py-2 border-b border-gray-200 dark:border-gray-700 sticky top-10 z-50 bg-white dark:bg-slate-800'>
            <div className='flex items-center space-x-2 overflow-x-auto'>
              <button
                onClick={handleNavigateToRoot}
                className='text-sm hover:underline text-teal-500 whitespace-nowrap'
              >
                Root
              </button>

              {(folderPath || []).map((folder, index) => (
                <Fragment key={folder.id}>
                  <span className='text-gray-500'>/</span>
                  {index === folderPath.length - 1 ? (
                    <span className='text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap'>{folder.name}</span>
                  ) : (
                    <button
                      onClick={() => handleNavigateToPathFolder(index)}
                      className='text-sm hover:underline text-teal-500 whitespace-nowrap'
                    >
                      {folder.name}
                    </button>
                  )}
                </Fragment>
              ))}
            </div>
          </div>

          {/* Folders Section */}
          <div className='px-4 py-3 border-b border-gray-200 dark:border-slate-700'>
            <h3 className='text-xs uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400 mb-3'>
              FOLDERS
            </h3>
            {storageFolders.length > 0 ? (
              <div className='grid grid-cols-3 gap-3'>
                {storageFolders.map((folder: IFolderBrief) => (
                  <div
                    key={folder._id}
                    className='flex items-center cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-700 py-2 px-2 rounded-sm'
                    onClick={() => handleNavigateToFolder(folder._id)}
                  >
                    <Folder width={16} height={16} className='text-teal-500 mr-2 shrink-0' />
                    <span className='text-sm truncate'>{folder._Name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className='text-sm text-gray-500 dark:text-gray-400'>No folders</div>
            )}
          </div>

          {/* Images Grid */}
          <div className='p-4 mk-scroll overflow-y-auto'>
            <h3 className='text-xs uppercase font-semibold tracking-wider text-gray-500 dark:text-gray-400 mb-3'>
              IMAGES
            </h3>
            <div className='grid grid-cols-4 gap-6'>
              {storage.items.length === 0 ? (
                <div className='col-span-4 flex text-gray-500 dark:text-gray-400'>No images found</div>
              ) : (
                storage.items.map((img, idx) => {
                  const name = img._Name
                  const size = img._Size
                  const smallSrc = GetImageMetdataLink(img._Link, ImageMetadata.XS)
                  const sz = getFileSize(size)

                  let currentSelected = typeof selectedImages === 'string' ? [selectedImages] : selectedImages || []
                  let isSelected = !!currentSelected.find((s) => (s.Url || s) === img._Link)

                  return (
                    <div
                      className='p-4 border bdr'
                      key={idx}
                      onClick={() => {
                        if (!multiple) {
                          onChange(ImageManagerKind.MyImages, img._Link)
                          onHide?.()
                        } else {
                          handleImageSelection(img, isSelected)
                        }
                      }}
                    >
                      <div className={clsx('mk-im-img', isSelected && 'selected', smallPreview && 'small')}>
                        <LazyImage failSrc={img._Link} src={smallSrc} />
                      </div>
                      <div className='txt font-semibold'>{name}</div>
                      <div className='text-light'>{t('Size: {{size}}', { size: sz })}</div>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </>
      )}
      <div
        className={clsx('border-t brd dark:border-slate-700', {
          'pb-[150px] pt-4': multiple && storage.items.length,
          'pb-[132px]': multiple && storage.items.length === 0,
          'pb-[1rem] pt-4': !multiple && storage.items.length,
          'pb-0': !multiple && storage.items.length === 0,
        })}
      >
        <WrappedPagination
          gap='none'
          key={page.toString()}
          totalCount={storage.totalCount}
          page={page}
          perPage={20}
          onChange={(_, __, n) => {
            if (page !== n) {
              handlePageChange(n)
            }
          }}
        />
      </div>

      {multiple && (
        <>
          <div className='border-t bdr p-4 justify-end flex-col flex absolute bottom-0 left-0 right-0 w-full bg-white dark:bg-slate-800'>
            {/* Footer checklist - Only show when multiple selection is enabled */}
            <div className='py-2'>
              <div className='flex items-center'>
                <div className='text-xs text-gray-500 dark:text-gray-400 mr-3'>
                  {selectedImages.length} {selectedImages.length === 1 ? 'image' : 'images'} selected
                </div>

                {selectedImages.length > 0 && (
                  <div className='overflow-x-auto max-w-md' style={{ scrollbarWidth: 'thin' }}>
                    <div className='flex flex-nowrap space-x-2 py-2 pr-2' style={{ width: 'max-content' }}>
                      {selectedImages.map((image) => {
                        const smallSrc = GetImageMetdataLink(image.Url, ImageMetadata.XS)

                        return (
                          <div key={image.Url} className='relative group shrink-0'>
                            <div className='w-8 h-8 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-sm overflow-hidden'>
                              <div className='w-full h-full flex items-center justify-center'>
                                <LazyImage src={smallSrc} failSrc={image.Url} alt={image.Title || 'Selected image'} />
                              </div>
                            </div>
                            <button
                              className='absolute -top-2 -right-2 w-5 h-5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center text-xs'
                              onClick={(e) => {
                                e.stopPropagation()
                                handleImageSelection({ _Link: image.Url } as IFilePresenter, true)
                              }}
                            >
                              ×
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className='flex items-center'>
              <Button
                variant={ButtonVariant.Outlined}
                onClick={() => {
                  onChange(ImageManagerKind.MyImages, initialSelectedRef.current)
                  onHide?.()
                }}
                className='mr-2'
              >
                <Trans>Cancel</Trans>
              </Button>

              <Button variant={ButtonVariant.Outlined} onClick={handleClear} className='w-20 mr-auto'>
                <Trans>Clear</Trans>
              </Button>
              <Button
                disabled={isSaveButtonDisabled}
                className='w-20'
                onClick={() => {
                  onChange(ImageManagerKind.MyImages, selectedImages)
                  onHide?.()
                }}
              >
                <Trans>Save</Trans>
              </Button>
            </div>
          </div>
        </>
      )}
    </Tabs.Panel>
  )
}
